import { useCallback, useState } from 'react';

import { selectHasActiveTurn, useChatStore } from '@/chat/hooks/use-chat-store';
import { useSuggestion } from '@/chat/hooks/use-suggestion';
import type { ComposerTab, SuggestionMode } from '@/chat/types';
import { PromptInput, type PromptInputMessage } from '@/components/ai-elements/prompt-input';
import { ACCEPTED_AUDIO_TYPES, ACCEPTED_MEDIA_TYPES, MAX_MEDIA_SIZE } from '@/upload/constants';
import { ComposerFields } from './composer-fields';
import { DragOverlay } from './drag-overlay';

const SUGGEST_REJECT_MESSAGES: Record<'accept' | 'max_file_size' | 'max_files', string> = {
  accept: '이미지, 영상 또는 음원 파일만 첨부할 수 있습니다',
  max_file_size: '100MB 이하 파일만 첨부할 수 있습니다',
  max_files: '파일은 한 번에 하나만 첨부할 수 있습니다',
};

const REPLACE_REJECT_MESSAGES: Record<'accept' | 'max_file_size' | 'max_files', string> = {
  accept: '음원 파일만 첨부할 수 있습니다',
  max_file_size: '100MB 이하 파일만 첨부할 수 있습니다',
  max_files: '파일은 한 번에 하나만 첨부할 수 있습니다',
};

export const Composer = () => {
  const { send, sendReplacement } = useSuggestion();
  const hasActiveTurn = useChatStore(selectHasActiveTurn);

  const [tab, setTab] = useState<ComposerTab>('suggest');
  const [hasText, setHasText] = useState(false);
  const [isStoryboard, setIsStoryboard] = useState(false);
  const [mode, setMode] = useState<SuggestionMode>('auto');
  const [rejectMessage, setRejectMessage] = useState<string | null>(null);
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);

  const handleSubmit = (message: PromptInputMessage) => {
    const filePart = message.files[0] ?? null;

    if (tab === 'replace') {
      if (filePart) {
        const text = message.text.trim() || null;
        void sendReplacement({ filePart, text });
      }
    } else {
      const text = message.text.trim() || null;
      const isImage = filePart?.mediaType?.startsWith('image/') ?? false;
      void send({ text, filePart, isStoryboard: isImage && isStoryboard, mode });
    }

    setHasText(false);
    setIsStoryboard(false);
    setRejectMessage(null);
    setNoticeMessage(null);
    // PromptInput의 form.reset()이 Radix Select를 초기화하므로 재설정
    setMode(mode);
  };

  // 인라인 콜백이면 리렌더마다 첨부 effect가 재실행되어 첨부 존재 중의
  // 거부 메시지(비허용 파일 드롭 등)를 즉시 지워버리므로 참조를 안정화
  const handleAttached = useCallback(() => setRejectMessage(null), []);

  const handleTabChange = (nextTab: ComposerTab) => {
    setTab(nextTab);
    setIsStoryboard(false);
    setMode('auto');
    setRejectMessage(null);
  };

  const handleNewChatConfirm = () => {
    setTab('suggest');
    setHasText(false);
    setIsStoryboard(false);
    setMode('auto');
    setRejectMessage(null);
    setNoticeMessage(null);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {/* 거부와 절단 안내는 동시에 설정될 수 있어 거부를 우선하는 단일 슬롯으로 표시 */}
      {rejectMessage ? (
        <p className="text-destructive text-sm">{rejectMessage}</p>
      ) : (
        noticeMessage && <p className="text-muted-foreground text-sm">{noticeMessage}</p>
      )}
      <DragOverlay />
      <PromptInput
        accept={tab === 'replace' ? ACCEPTED_AUDIO_TYPES.join(',') : ACCEPTED_MEDIA_TYPES.join(',')}
        globalDrop
        maxFiles={1}
        maxFileSize={MAX_MEDIA_SIZE}
        onError={(err) =>
          setRejectMessage(
            (tab === 'replace' ? REPLACE_REJECT_MESSAGES : SUGGEST_REJECT_MESSAGES)[err.code]
          )
        }
        onSubmit={handleSubmit}
      >
        {/* PromptInputBody(display: contents)로 감싸면 InputGroup의 직계 자식 셀렉터가
            깨져 세로 배치가 무너지므로 fragment를 직접 렌더 */}
        <ComposerFields
          tab={tab}
          onTabChange={handleTabChange}
          hasActiveTurn={hasActiveTurn}
          hasText={hasText}
          isStoryboard={isStoryboard}
          mode={mode}
          onAttached={handleAttached}
          onModeChange={setMode}
          onNewChatConfirm={handleNewChatConfirm}
          onNotice={setNoticeMessage}
          onReject={setRejectMessage}
          onTextChange={setHasText}
          onToggleStoryboard={() => setIsStoryboard((prev) => !prev)}
        />
      </PromptInput>
    </div>
  );
};
